import { useEffect } from 'react';
import { TOP_BAR_ATTRIBUTE } from './chrome';
import { DOCK_ATTRIBUTE } from './useEditingOutsideDock';

/**
 * Stops the page behind an overlay from scrolling while `locked`.
 *
 * `overflow: hidden` on <html> (the document scroller here), which keeps the
 * scroll position: the page is where it was when the overlay closes. Like MUI's
 * Modal, it puts back exactly the inline values it found, and, where a classic
 * scrollbar takes layout space, pads the page (and the fixed top bar and dock)
 * by the scrollbar's width so the content does not jump sideways.
 * `overscroll-behavior` on the overlay alone is not enough: it does nothing
 * when the overlay is too short to scroll, and a drag on it then scrolls the
 * page.
 */
export function useDocumentScrollLock(locked: boolean): void {
    useEffect(() => {
        if (!locked) return;
        const root = document.documentElement;
        const { overflow } = root.style;
        // Measured before the lock takes the scrollbar away.
        const scrollbar = window.innerWidth - root.clientWidth;
        root.style.overflow = 'hidden';
        // The fixed bars are as wide as the window, so they grow by the
        // scrollbar's width too, and their icons would shift: MUI pads its
        // `.mui-fixed` elements the same way.
        const padded = scrollbar > 0 ? [root, ...fixedBars()] : [];
        const before = padded.map(el => el.style.paddingRight);
        padded.forEach(el => {
            const padding = parseFloat(getComputedStyle(el).paddingRight) || 0;
            el.style.paddingRight = `${padding + scrollbar}px`;
        });
        return () => {
            root.style.overflow = overflow;
            padded.forEach((el, i) => {
                el.style.paddingRight = before[i];
            });
        };
    }, [locked]);
}

const fixedBars = () =>
    Array.from(
        document.querySelectorAll<HTMLElement>(
            `[${TOP_BAR_ATTRIBUTE}], [${DOCK_ATTRIBUTE}]`
        )
    );
